import React from 'react';

import MediaImageThumbnail from '@atom/components/common/MediaImageThumbnail';
import csvIcon from '@atom/components/common/svgIcons/csvIcon.svg';
import elementIcon from '@atom/components/common/svgIcons/elementIcon.svg';
import elementIconBlue from '@atom/components/common/svgIcons/elementIconBlue.svg';
import elementIconRed from '@atom/components/common/svgIcons/elementIconRed.svg';
import pdfIcon from '@atom/components/common/svgIcons/pdfIcon.svg';
import wordIcon from '@atom/components/common/svgIcons/wordIcon.svg';
import { Icon } from '@atom/mui';
import {
  ArrowDropDownIcon,
  FolderIcon,
  InsertDriveFileIcon,
  MovieIcon,
  ReportProblemIcon,
  VerifiedUserIcon,
  WorkIcon,
} from '@atom/mui/icons';
import {
  DEFECT_MARKER_ID,
  PROTECTIVE_COATING_MARKER_ID,
} from '@atom/selectors/schemaSelectors';
import colors from '@atom/styles/colors';
import { MediaType } from '@atom/types/media';

import schemaUtilities from './schemaUtilities';
import workOrderStatusUtilities from './workOrderStatusUtilities';

import leftPaneStyles from '../styles/left-pane.module.css';

/**
 *
 * @param {string} [iconType=none] The type of icon to display.
 *
 * If anything other than 'down-arrow' or 'right-arrow' is used, the default will be 'none'
 *
 * Available Options:
 * "down-arrow"
 * "right-arrow"
 * "none"
 *
 * @returns {ReactElement}
 */
export function leftIcon(iconType) {
  if (iconType !== 'down-arrow' && iconType !== 'right-arrow') {
    return <div />;
  }

  const rotation = iconType !== 'down-arrow' ? 'rotate' : '';
  return (
    <Icon
      className={rotation ? leftPaneStyles.rotate : undefined}
      color={colors.neutral.dim}
      icon={ArrowDropDownIcon}
    />
  );
}

/**
 *
 * @param {string} [iconType=none] The type of icon to display.
 * @param {object} [style] additional icon style properties
 *
 * If anything other than 'down-arrow' or 'right-arrow' is used, the default will be 'none'
 *
 * Available Options:
 * "down-arrow"
 * "right-arrow"
 * "none"
 *
 * @returns {ReactElement}
 */
export function leftFolderIcon(iconType, style?: React.CSSProperties) {
  if (iconType !== 'down-arrow' && iconType !== 'right-arrow') {
    return <div />;
  }

  const rotation = iconType !== 'down-arrow' ? 'rotate' : '';
  return (
    <div style={{ marginRight: '10px' }}>
      <Icon
        className={rotation ? leftPaneStyles.rotate : undefined}
        color={colors.neutral.dim}
        icon={ArrowDropDownIcon}
      />
      <Icon
        style={{ ...style }}
        color={colors.neutral.gray}
        icon={FolderIcon}
      />
    </div>
  );
}

export const buildElementIcon = (markerId, style, isBlue) => {
  switch (markerId) {
    case DEFECT_MARKER_ID: {
      const styles = isBlue
        ? { color: colors.brand.blue, ...style.fhwaStyle }
        : style.fhwaStyle;

      return <Icon style={styles} icon={ReportProblemIcon} />;
    }
    case PROTECTIVE_COATING_MARKER_ID: {
      const styles = isBlue
        ? { color: colors.brand.blue, ...style.fhwaStyle }
        : style.fhwaStyle;

      return <Icon style={styles} icon={VerifiedUserIcon} />;
    }
    default: {
      const src = isBlue ? elementIconBlue : elementIcon;
      return <img style={style.imageStyle} src={src} />;
    }
  }
};

export const buildFHWAElementIcon = (markerId, isValid) => {
  const iconStyles = isValid
    ? { marginRight: '0.5rem', color: colors.neutral.gray }
    : { marginRight: '0.5rem', color: colors.brand.red };

  const fhwaElementIcon = isValid ? elementIcon : elementIconRed;

  switch (markerId) {
    case DEFECT_MARKER_ID: {
      return <Icon style={iconStyles} icon={ReportProblemIcon} />;
    }
    case PROTECTIVE_COATING_MARKER_ID: {
      return <Icon style={iconStyles} icon={VerifiedUserIcon} />;
    }
    default: {
      return <img style={{ marginRight: '0.5rem' }} src={fhwaElementIcon} />;
    }
  }
};

export function leftElementIcon(markerId, iconType, isBlue) {
  if (iconType !== 'down-arrow' && iconType !== 'right-arrow') {
    return <div />;
  }

  const rotation = iconType !== 'down-arrow' ? 'rotate' : '';
  const imageStyle = { margin: '0 0 0 6px' };
  const fhwaStyle = {
    margin: '0 0 0 6px',
    color: colors.neutral.gray,
  };

  const styles = { imageStyle, fhwaStyle };
  const newElementIcon = buildElementIcon(markerId, styles, isBlue);

  return (
    <div
      style={{
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
      }}
    >
      <Icon
        className={rotation ? leftPaneStyles.rotate : undefined}
        color={colors.neutral.dim}
        icon={ArrowDropDownIcon}
      />
      {newElementIcon}
    </div>
  );
}

export function getElementIcon(markerId, isBlue) {
  const imageStyle = { margin: '0 0 0 30px' };
  const fhwaStyle = {
    margin: '0 0 0 30px',
    color: colors.neutral.gray,
  };

  const styles = { imageStyle, fhwaStyle };

  return buildElementIcon(markerId, styles, isBlue);
}

export function assetFolderIcon(schemaColorId, style?: React.CSSProperties) {
  const color = schemaUtilities.getSchemaColorFromColorId(schemaColorId);

  return (
    <Icon
      style={{
        bottom: '10px',
        marginLeft: '30px',
        ...style,
      }}
      color={color}
      icon={FolderIcon}
    />
  );
}

export const getMediaIcon = (
  type: MediaType,
  fileExtension: string,
  fileId: string,
  name: string,
) => {
  switch (true) {
    case fileExtension === 'pdf': {
      return <img className="pdf-icon" src={pdfIcon} />;
    }
    case fileExtension === 'csv': {
      return <img className="csv-icon" src={csvIcon} />;
    }
    case fileExtension === 'doc':
    case fileExtension === 'docx': {
      return <img className="word-icon" src={wordIcon} />;
    }
    case fileExtension === 'folder': {
      return <Icon color={colors.neutral.gray} icon={FolderIcon} />;
    }
    case type === MediaType.IMAGE: {
      return <MediaImageThumbnail fileId={fileId} name={name} />;
    }
    case type === MediaType.VIDEO: {
      return <Icon color={colors.brand.red} icon={MovieIcon} />;
    }
    default: {
      return <Icon color={colors.brand.blue} icon={InsertDriveFileIcon} />;
    }
  }
};

export function getWorkIconByStatusId(statusId) {
  const color =
    workOrderStatusUtilities.getWorkOrderStatusColor(statusId) ||
    colors.neutral.gray;

  return <Icon color={color} icon={WorkIcon} />;
}

export function getDataManagementElementIcon(
  markerId: number,
  isSelected: boolean,
  style?: React.CSSProperties,
) {
  const defaultIcon = isSelected ? elementIconBlue : elementIcon;
  const color = isSelected ? colors.brand.blue : colors.neutral.gray;

  switch (markerId) {
    case DEFECT_MARKER_ID:
    case PROTECTIVE_COATING_MARKER_ID: {
      const icon =
        markerId === DEFECT_MARKER_ID ? ReportProblemIcon : VerifiedUserIcon;
      return <Icon style={{ margin: 0 }} color={color} icon={icon} />;
    }
    default: {
      return <img style={{ fontWeight: 300, ...style }} src={defaultIcon} />;
    }
  }
}

export const fullCategoryLeftTreeIcon = () => {
  return (
    <>
      <Icon icon={ArrowDropDownIcon} />
      <Icon icon={FolderIcon} style={{ marginLeft: '0.5rem' }} />
    </>
  );
};

const iconUtilities = {
  leftIcon,
  getElementIcon,
  getMediaIcon,
  leftFolderIcon,
  leftElementIcon,
  assetFolderIcon,
  getWorkIconByStatusId,
  getDataManagementElementIcon,
  buildElementIcon,
  buildFHWAElementIcon,
  fullCategoryLeftTreeIcon,
  DEFECT_MARKER_ID,
  PROTECTIVE_COATING_MARKER_ID,
};

export default iconUtilities;
